import { colors } from '@fitpet/constants-global'
import { FitpetIcon } from '@fitpet/ui/components'
import { clsx } from 'clsx'
import { useDispatch } from 'react-redux'
import { match, P } from 'ts-pattern'
import { ProductLocation } from '@/constants/product-location.const'
import { useCouponEventTracker } from '@/containers/event-tracker/coupon.event-tracker'
import { doSetBottomSheetData, doSetProductCouponDownloadBottomSheetData } from '@/stores/reduxData'
import { doSetToggleModalLogin } from '@/stores/reduxUI'
import { currencyText, percentText } from '@/utils/utilNumber'
import styles from './ProductDetailPriceInformation.module.css'

interface ProductDetailPriceInformationProps {
  isUserLoggedIn: boolean
  productId: number
  productName: string
  promotionId?: number
  hasApplicableProductCoupon: boolean
  hasUnreceivedProductCoupon: boolean
  discountRateAfterCoupon: number
  price: number
  salePrice: number
  salePriceAfterCoupon: number
  salePriceAfterWelcomeCoupon?: number
  discountRate: number
  maxOptionSkuCount: number
  pricePerSku: number
  isRealSoldOut: boolean
  expectedShippingFee: number
}

interface CouponPriceSectionProps {
  discountRateAfterCoupon: number
  price: number
  salePrice: number
  salePriceAfterCoupon: number
  onClickCouponDownload: ({ buttonText }: { buttonText: string }) => void
  hasUnreceivedProductCoupon: boolean
}

interface NoCouponPriceSectionProps {
  price: number
  salePrice: number
  discountRate: number
  isShowOriginalPrice: boolean
}

interface WelcomDealCouponPriceSectionProps {
  price: number
  salePrice: number
  discountRate: number
  isShowOriginalPrice: boolean
  salePriceAfterWelcomeCoupon: number
}

const CouponPriceSection = ({
  discountRateAfterCoupon,
  price,
  salePrice,
  salePriceAfterCoupon,
  onClickCouponDownload,
  hasUnreceivedProductCoupon,
}: CouponPriceSectionProps) => {
  return (
    <>
      <div className={styles.couponsPriceSection}>
        {discountRateAfterCoupon > 0 && (
          <span className="font-body3-medium gray-500">{percentText(discountRateAfterCoupon)}</span>
        )}
        <div className={clsx(styles.priceWithStrikethrough, 'font-body3-regular gray-300')}>
          {price.toLocaleString('ko-KR')}원
        </div>
      </div>
      <div className={clsx(styles.couponSalePrice, 'font-heading3-bold gray-900')}>
        {salePrice.toLocaleString('ko-KR')}원
      </div>
      <div className={styles.couponDiscountPriceSection}>
        <div className={styles.couponDiscountPrice}>
          <span className="font-heading3-bold red-500">{salePriceAfterCoupon.toLocaleString('ko-KR')}원</span>
          <span className="font-body4-medium red-500">쿠폰적용시</span>
        </div>
        {hasUnreceivedProductCoupon ? (
          <button
            type="button"
            className={styles.couponDownloadButton}
            onClick={() =>
              onClickCouponDownload({
                buttonText: '쿠폰 받기',
              })
            }
          >
            <span className="font-body3-medium gray-white">쿠폰 받기</span>
            <FitpetIcon icon="icDownload" size={16} color={colors.WHITE} />
          </button>
        ) : (
          <button
            type="button"
            className={styles.viewReceivedCouponButton}
            onClick={() =>
              onClickCouponDownload({
                buttonText: '받은 쿠폰 보기',
              })
            }
          >
            <span className="font-body3-medium gray-900">받은 쿠폰 보기</span>
            <FitpetIcon icon="icChevronRight" size={16} color={colors.GRAY_900} />
          </button>
        )}
      </div>
    </>
  )
}

const NoCouponPriceSection = ({ price, salePrice, discountRate, isShowOriginalPrice }: NoCouponPriceSectionProps) => {
  return (
    <>
      {match(isShowOriginalPrice)
        .with(true, () => {
          return <div className="font-heading3-bold gray-900">{price.toLocaleString('ko-KR')}원</div>
        })
        .with(false, () => {
          return (
            <>
              <div className={clsx(styles.priceWithStrikethrough, 'font-body3-regular gray-300')}>
                {price.toLocaleString('ko-KR')}원
              </div>
              <div className={styles.noCouponSalesPriceSection}>
                <div className="font-heading3-bold red-500">{percentText(discountRate)}</div>
                <div className="font-heading3-bold gray-900">{salePrice.toLocaleString('ko-KR')}원</div>
              </div>
            </>
          )
        })
        .exhaustive()}
    </>
  )
}

const WelcomDealCouponPriceSection = ({
  price,
  salePrice,
  salePriceAfterWelcomeCoupon,
  discountRate,
}: WelcomDealCouponPriceSectionProps) => {
  return (
    <>
      <div className={clsx(styles.priceWithStrikethrough, 'font-body3-regular gray-300')}>
        {price.toLocaleString('ko-KR')}원
      </div>
      <div className={styles.noCouponSalesPriceSection}>
        <div className="font-heading3-bold red-500">{percentText(discountRate)}</div>
        <div className="font-heading3-bold gray-900">{salePrice.toLocaleString('ko-KR')}원</div>
      </div>
      <div className={styles.couponDiscountPrice}>
        <span className="font-heading3-bold red-500">{salePriceAfterWelcomeCoupon.toLocaleString('ko-KR')}원</span>
        <span className="font-body4-medium red-500">웰컴딜 쿠폰적용시</span>
      </div>
    </>
  )
}

export const ProductDetailPriceInformation = ({
  isUserLoggedIn,
  productId,
  productName,
  promotionId,
  hasApplicableProductCoupon,
  hasUnreceivedProductCoupon,
  discountRateAfterCoupon,
  price,
  salePrice,
  salePriceAfterCoupon,
  salePriceAfterWelcomeCoupon,
  discountRate,
  maxOptionSkuCount,
  pricePerSku,
  isRealSoldOut,
  expectedShippingFee,
}: ProductDetailPriceInformationProps) => {
  const dispatch = useDispatch()
  const { trackClickListCouponDownloadEvent } = useCouponEventTracker()

  const handleClickCouponDownload = ({ buttonText }: { buttonText: string }) => {
    if (!isUserLoggedIn) {
      dispatch(doSetToggleModalLogin(true))
      return
    }
    dispatch(
      doSetBottomSheetData({
        isProductCouponDownloadVisible: true,
      })
    )

    dispatch(
      doSetProductCouponDownloadBottomSheetData({
        productId,
        promotionId,
        productName,
        isRealSoldOut,
        buttonText,
        addLocation: ProductLocation.ProductDetail,
      })
    )

    trackClickListCouponDownloadEvent({
      addLocation: ProductLocation.ProductDetail,
      productId,
      productName,
      buttonText,
      hasApplicableProductCoupon,
      salePriceAfterCoupon,
      isRealSoldOut,
    })
  }

  const isShowOriginalPrice = price === salePrice
  const isShowBundleMessage = maxOptionSkuCount > 1 && pricePerSku > 0

  const hasWelcomeCoupon = salePriceAfterWelcomeCoupon !== undefined && salePriceAfterWelcomeCoupon > 0

  return (
    <>
      {match([hasApplicableProductCoupon, hasWelcomeCoupon])
        .with([P._, true], () => {
          return (
            <WelcomDealCouponPriceSection
              price={price}
              salePrice={salePrice}
              discountRate={discountRate}
              salePriceAfterWelcomeCoupon={salePriceAfterWelcomeCoupon || 0}
              isShowOriginalPrice={isShowOriginalPrice}
            />
          )
        })
        .with([true, false], () => {
          return (
            <CouponPriceSection
              hasUnreceivedProductCoupon={hasUnreceivedProductCoupon}
              discountRateAfterCoupon={discountRateAfterCoupon}
              price={price}
              salePrice={salePrice}
              salePriceAfterCoupon={salePriceAfterCoupon}
              onClickCouponDownload={handleClickCouponDownload}
            />
          )
        })
        .with([false, false], () => {
          return (
            <NoCouponPriceSection
              price={price}
              salePrice={salePrice}
              discountRate={discountRate}
              isShowOriginalPrice={isShowOriginalPrice}
            />
          )
        })
        .exhaustive()}
      {isShowBundleMessage && (
        <div className={clsx(styles.skuPerPrice, 'font-body4-regular blue-800')}>
          {maxOptionSkuCount}개 구매 시 각 {pricePerSku.toLocaleString('ko-KR')}원
        </div>
      )}
      <div className={styles.shippingFee}>
        <FitpetIcon icon="icShippingFee" size={16} color={colors.GRAY_300} />
        <div className="font-body4-bold gray-300">
          {expectedShippingFee > 0 ? currencyText(expectedShippingFee) : '무료배송'}
        </div>
      </div>
    </>
  )
}
